import { useState } from 'react';

import { useUIContext } from 'lib/context/UIContext/UIContext';
import { cn, ia, iaRa } from 'lib/helpers/utility';

import { groupStatusContent } from '../../lib/constants';
import {
  getFilteredInvoices,
  mergeAllRows,
  splitInvoicesByDate,
  updateShowFlag,
  updateShowFlagByDate
} from '../../lib/helpers';
import TableFooter from '../TableFooter';
import TableHeaderRow from '../TableHeaderRow';

import TableGfeActions from './TableGfeActions';
import TableGfeDetails from './TableGfeDetails';
import TableGfeInvoices from './TableGfeInvoices';

const TableGfeContent = ({
  visibleCols,
  invoiceId,
  invoices,
  statusGroup,
  setRowData,
  preview,
  openModal,
  invoice,
  CustomRowHeader,
  checkin,
  footer,
  variant
}) => {
  const { device } = useUIContext();
  const isMobile = device === 'tablet-sm' || device === 'mobile';

  const [isGfeChecked, setIsGfeChecked] = useState(ia(invoices));

  const { pastInvoices, upcomingInvoices } = splitInvoicesByDate(iaRa(invoices));

  const filteredPastInvoices = getFilteredInvoices(pastInvoices, true);

  const filteredUpcomingInvoices = getFilteredInvoices(upcomingInvoices, true);

  const onCheckedChange = (status, checked) => {
    setIsGfeChecked(checked);
    setRowData((prev) =>
      prev.map((item) => {
        if (item.id === invoiceId)
          return {
            ...item,
            show: checked,
            procedures: updateShowFlag(item.procedures, status, checked),
            products: updateShowFlag(item.products, status, checked),
            packages: updateShowFlag(item.packages, status, checked)
          };

        return item;
      })
    );
  };

  const onActionCheckedChange = (status, checked, isPast) => {
    const today = new Date().toISOString();

    const isRelevantInvoices = isPast
      ? checked || ia(getFilteredInvoices(upcomingInvoices, !checked))
      : checked || ia(getFilteredInvoices(pastInvoices, !checked));

    setIsGfeChecked(isRelevantInvoices);

    setRowData((prev) =>
      prev.map((item) => {
        if (item.id === invoiceId)
          return {
            ...item,
            show: isRelevantInvoices,
            procedures: updateShowFlagByDate(item.procedures, status, today, checked, isPast),
            products: updateShowFlagByDate(item.products, status, today, checked, isPast),
            packages: updateShowFlagByDate(item.packages, status, today, checked, isPast)
          };

        return item;
      })
    );
  };

  return (
    <tr>
      <td colSpan={visibleCols.length} className="p-0">
        <div
          className={cn(
            'w-full overflow-x-auto rounded-xl border border-solid border-primary-300 bg-primary-25 print:overflow-visible',
            isMobile && 'rounded-none border-0 border-b'
          )}>
          <table className="h-fit w-full max-w-full border-collapse">
            {!isMobile && (
              <colgroup>
                {visibleCols.map((col, idx) => (
                  <col
                    key={idx}
                    style={{
                      minWidth: col.minWidth || 80,
                      width: col.width || 'auto'
                    }}
                  />
                ))}
              </colgroup>
            )}
            <tbody>
              <tr>
                <th colSpan={visibleCols.length} className="p-0">
                  <TableGfeDetails
                    visibleCols={visibleCols}
                    statusGroup={statusGroup}
                    onCheckedChange={onCheckedChange}
                    isChecked={isGfeChecked}
                    preview={preview}
                    checkin={checkin}
                    variant={variant}
                  />
                </th>
              </tr>
              {!isMobile && (
                <TableHeaderRow columnDefs={visibleCols} preview={preview} variant={variant} />
              )}
              <TableGfeInvoices
                title={groupStatusContent.Past.value}
                icon={groupStatusContent.Past.icon}
                visibleCols={visibleCols}
                invoices={pastInvoices}
                statusGroup={statusGroup}
                preview={preview}
                openModal={openModal}
                invoice={invoice}
                CustomRowHeader={CustomRowHeader}
                checkin={checkin}
                variant={variant}
                footer={footer}
                actions={
                  !preview && (
                    <TableGfeActions
                      statusGroup={statusGroup}
                      onCheckedChange={onActionCheckedChange}
                      checked={ia(filteredPastInvoices) && isGfeChecked}
                      isPast
                    />
                  )
                }
              />
              <TableGfeInvoices
                title={groupStatusContent.Future.value}
                icon={groupStatusContent.Future.icon}
                description={groupStatusContent.Future.description}
                headerBodyClassName="flex-row sm:flex-col"
                visibleCols={visibleCols}
                invoices={upcomingInvoices}
                statusGroup={statusGroup}
                preview={preview}
                openModal={openModal}
                invoice={invoice}
                CustomRowHeader={CustomRowHeader}
                checkin={checkin}
                variant={variant}
                footer={footer}
                actions={
                  !preview && (
                    <TableGfeActions
                      statusGroup={statusGroup}
                      onCheckedChange={onActionCheckedChange}
                      checked={ia(filteredUpcomingInvoices) && isGfeChecked}
                    />
                  )
                }
              />
            </tbody>
            {ia(invoices) && footer && !isMobile && (
              <tfoot>
                <TableFooter
                  columnDefs={visibleCols}
                  rowData={mergeAllRows(iaRa(invoices))}
                  variant={variant}
                />
              </tfoot>
            )}
          </table>
        </div>
      </td>
    </tr>
  );
};

export default TableGfeContent;
